/* Frequency Selector — powers <frequency-selector> (snippets/frequency-selector.liquid).
   Picks a selling plan, reads the color variant from the PDP `variant:changed`
   event, and adds to cart (razor + bundled blades on "add-blades" cadences).
   Follows the same OOS / waitlist cascade as the buy_button block: hidden /
   pseudo OOS variants get a disabled "Sold Out" button, true OOS variants get
   "Join the Waitlist" (opens the BIS modal) unless the product is no-waitlist. */

(function () {
  'use strict';

  if (window.customElements && customElements.get('frequency-selector')) return;

  class FrequencySelector extends HTMLElement {
    connectedCallback() {
      if (this.dataset.inited === '1') return;
      this.dataset.inited = '1';

      this.opts = Array.prototype.slice.call(
        this.querySelectorAll('.freq-selector__opt')
      );
      this.bladesVariantId = this.getAttribute('data-blades-variant-id') || '';
      this.bladesSellingPlanId = this.getAttribute('data-blades-selling-plan-id') || '';
      // True when the PDP product itself is the blades product (blades quiz); its annual option adds only the 24-pack (no separate kit line).
      this.mainIsBlades = this.getAttribute('data-main-is-blades') === 'true';
      this.variantId = this.getAttribute('data-variant-id') || '';
      this.basePrice = parseInt(this.getAttribute('data-base-price'), 10) || 0;
      this.bundlePrice = parseInt(this.getAttribute('data-bundle-price'), 10) || 0;

      // Variant id → price (cents) map: base price tracks the selected color.
      this.variantPrices = {};
      var pricesEl = this.querySelector('[data-freq-variant-prices]');
      if (pricesEl) {
        try { this.variantPrices = JSON.parse(pricesEl.textContent || '{}'); } catch (e) { /* ignore */ }
      }

      // Variant id → {isOos, isTrueOos} so the button follows the chosen color (see snippets/variant-oos-state.liquid).
      this.variantStates = {};
      var statesEl = this.querySelector('[data-freq-variant-states]');
      if (statesEl) {
        try { this.variantStates = JSON.parse(statesEl.textContent || '{}'); } catch (e) { /* ignore */ }
      }
      this.noWaitlist = this.getAttribute('data-no-waitlist') === 'true';
      this.atcBtn = this.querySelector('[data-freq-atc]');
      this.isOos = !!(this.atcBtn && this.atcBtn.getAttribute('data-variant-oos') === 'true');
      this.showWaitlist = !!(this.atcBtn && this.atcBtn.getAttribute('data-variant-waitlist') === 'true');

      // One-time product variants (title → {id, price, image}) so a one-time option follows the chosen color.
      this.onetimeVariants = {};
      var onetimeEl = this.querySelector('[data-freq-onetime-variants]');
      if (onetimeEl) {
        try { this.onetimeVariants = JSON.parse(onetimeEl.textContent || '{}'); } catch (e) { /* ignore */ }
      }

      var self = this;
      this.opts.forEach(function (btn) {
        btn.addEventListener('click', function () { self.select(btn); });
      });

      document.addEventListener('variant:changed', function (e) {
        var d = e && e.detail;
        var variant = d && d.variant;
        var id = d && ((variant && variant.id) || d.variantId);
        if (!id) return;
        self.variantId = String(id);
        if (variant && variant.title) {
          self.setAttribute('data-track-item-variant', variant.title);
        }
        if (self.variantPrices[self.variantId] != null) {
          self.basePrice = self.variantPrices[self.variantId];
        }
        self.syncOnetimeVariant(d && d.variant && d.variant.title);
        self.applyOosState();
        self.updatePrice();
      });

      this.syncInitialVariant();
      this.applyOosState();
      this.updatePrice();

      var atc = this.atcBtn;
      if (!atc) return;
      atc.addEventListener('click', function () {
        // OOS variants never add to cart: waitlist opens the BIS modal, everything else is inert.
        if (self.isOos) {
          if (self.showWaitlist) self.openWaitlist();
          return;
        }
        var original = atc.innerHTML;
        atc.setAttribute('disabled', 'disabled');
        atc.setAttribute('aria-busy', 'true');
        atc.textContent = 'Adding…';
        self.addToCart()
          .catch(function (err) {
            console.error('[frequency-selector] Add to cart failed:', err);
          })
          .then(function () {
            atc.removeAttribute('disabled');
            atc.removeAttribute('aria-busy');
            atc.innerHTML = original;
            // The label markup was swapped out for "Adding…", so re-apply anything that
            // changed (colour / OOS state) while the request was in flight.
            self.applyOosState();
            self.updatePrice();
          });
      });
    }

    select(btn) {
      this.opts.forEach(function (o) { o.setAttribute('aria-pressed', 'false'); });
      btn.setAttribute('aria-pressed', 'true');
      this.updatePrice();

      var freqEl = btn.querySelector('.freq-selector__freq');
      this.dispatchEvent(new CustomEvent('frequency-selector:change', {
        bubbles: true,
        detail: {
          sellingPlanId: btn.getAttribute('data-selling-plan-id') || '',
          label: freqEl ? freqEl.textContent.trim() : ''
        }
      }));
    }

    // Point the one-time card(s) at the variant matching the chosen color (by title):
    // variant id + price (for ATC), the panel image, and the price in the subtext.
    syncOnetimeVariant(title) {
      if (!title || !this.onetimeVariants) return;
      var v = this.onetimeVariants[title];
      if (!v) return;
      var self = this;
      this.querySelectorAll('.freq-selector__opt[data-one-time="true"]').forEach(function (card) {
        if (v.id != null) card.setAttribute('data-onetime-variant-id', v.id);
        if (v.price != null) card.setAttribute('data-onetime-price', v.price);
        if (v.image) {
          var img = card.querySelector('[data-freq-onetime-img]');
          if (img) img.setAttribute('src', v.image);
        }
        if (v.price != null) {
          var sub = card.querySelector('[data-freq-onetime-subtext-price]');
          if (sub) sub.textContent = self.formatMoney(v.price);
        }
      });
    }

    // <pdp-product> upgrades while the page parses and fires its initial `variant:changed`
    // before this deferred script runs, so read the live selection off it on init. Matters when
    // a ?variant= URL lands on a different colour than the server-side preselected one.
    syncInitialVariant() {
      var host = this.closest('pdp-product') || document.querySelector('pdp-product');
      if (!host) return;
      var variant = typeof host.getCurrentVariant === 'function' ? host.getCurrentVariant() : null;
      var id = (variant && variant.id) || host.getAttribute('selected-variant-id');
      if (!id) return;
      this.variantId = String(id);
      if (this.variantPrices[this.variantId] != null) {
        this.basePrice = this.variantPrices[this.variantId];
      }
      var title = (variant && variant.title) || host.getAttribute('selected-variant-title');
      if (title) {
        this.setAttribute('data-track-item-variant', title);
        this.syncOnetimeVariant(title);
      }
    }

    // Re-point the button at the selected variant's OOS state: sellable → "Add to Cart",
    // hidden / pseudo OOS → disabled "Sold Out", true OOS → "Join the Waitlist" (BIS modal).
    applyOosState() {
      var btn = this.atcBtn;
      if (!btn) return;
      var state = this.variantStates && this.variantStates[this.variantId];
      // No data for this variant id — keep whatever the server rendered.
      if (!state) return;
      this.isOos = !!state.isOos;
      this.showWaitlist = !!state.isTrueOos && !this.noWaitlist;

      var labelEl = btn.querySelector('[data-freq-atc-label]');
      var sepEl = btn.querySelector('[data-freq-atc-sep]');
      var priceEl = btn.querySelector('[data-freq-atc-price]');
      var showPrice = !this.isOos || this.showWaitlist;

      if (labelEl) {
        labelEl.textContent = this.showWaitlist
          ? (btn.getAttribute('data-label-waitlist') || 'Join the Waitlist')
          : this.isOos
            ? (btn.getAttribute('data-label-sold-out') || 'Sold Out')
            : (btn.getAttribute('data-label-atc') || 'Add to Cart');
      }
      if (sepEl) sepEl.hidden = !showPrice;
      if (priceEl) priceEl.hidden = !showPrice;

      if (this.isOos) {
        btn.setAttribute('data-variant-oos', 'true');
      } else {
        btn.removeAttribute('data-variant-oos');
      }
      if (this.showWaitlist) {
        btn.setAttribute('data-variant-waitlist', 'true');
      } else {
        btn.removeAttribute('data-variant-waitlist');
      }
      // Only the non-waitlist OOS states are truly inert; the waitlist button stays clickable.
      if (this.isOos && !this.showWaitlist) {
        btn.setAttribute('disabled', 'disabled');
      } else {
        btn.removeAttribute('disabled');
      }
      this.classList.toggle('is-oos', this.isOos);
    }

    openWaitlist() {
      if (!window.BISModal || !this.variantId) return;
      var styles = getComputedStyle(this);
      window.BISModal.open(this.variantId, {
        colors: {
          interior: styles.getPropertyValue('--buy-button-interior').trim() || '#FF2E91',
          exterior: styles.getPropertyValue('--buy-button-exterior').trim() || '#AE0B58',
          text: styles.getPropertyValue('--buy-button-text').trim() || '#FFFFFF'
        }
      });
      if (window.SiteAnalytics && typeof window.SiteAnalytics.trackAddToCart === 'function') {
        window.SiteAnalytics.trackAddToCart({
          item_id: this.variantId,
          item_name: this.getAttribute('data-track-item-name') || '',
          item_variant: this.getAttribute('data-track-item-variant') || '',
          source_component: this.getAttribute('data-track-source') || '',
          price: this.basePrice / 100,
          oos: true
        });
      }
    }

    updatePrice() {
      var priceEl = this.querySelector('[data-freq-atc-price]');
      if (!priceEl) return;
      var cents;
      if (this.isOneTime()) {
        // One-time option: show the one-time product's price.
        var pressed = this.querySelector('.freq-selector__opt[aria-pressed="true"]');
        var onetimePrice = pressed && parseInt(pressed.getAttribute('data-onetime-price'), 10);
        cents = onetimePrice || this.basePrice;
      } else if (this.mainIsBlades && this.shouldBundle()) {
        // Blades quiz with the annual ("Occasionally") option selected. On the razor kit this
        // option adds the kit AND a 24-pack of blades, so its price is basePrice + bundlePrice.
        // The blades quiz has no separate kit — the blades product is what's being sold — so only
        // the 24-pack goes in the cart, and the button shows just the 24-pack price (bundlePrice).
        cents = this.bundlePrice;
      } else {
        cents = this.basePrice + (this.shouldBundle() ? this.bundlePrice : 0);
      }
      priceEl.textContent = this.formatMoney(cents);
    }

    formatMoney(cents) {
      try {
        if (window.Shopify && typeof Shopify.formatMoney === 'function') {
          return Shopify.formatMoney(cents);
        }
      } catch (e) { /* fall through */ }
      return '$' + (cents / 100).toFixed(2);
    }

    currentPlanId() {
      var pressed = this.querySelector('.freq-selector__opt[aria-pressed="true"]');
      return pressed ? (pressed.getAttribute('data-selling-plan-id') || '') : '';
    }

    currentFrequencyCadence() {
      var pressed = this.querySelector('.freq-selector__opt[aria-pressed="true"]');
      return pressed ? (pressed.getAttribute('data-frequency-cadence') || '') : '';
    }

    shouldBundle() {
      if (!this.bladesVariantId) return false;
      if (this.isOneTime()) return false;
      var pressed = this.querySelector('.freq-selector__opt[aria-pressed="true"]');
      return !!(pressed && pressed.getAttribute('data-add-blades') === 'true');
    }

    isOneTime() {
      var pressed = this.querySelector('.freq-selector__opt[aria-pressed="true"]');
      return !!(pressed && pressed.getAttribute('data-one-time') === 'true');
    }

    trackAddToCartAnalytics() {
      if (!window.SiteAnalytics || typeof window.SiteAnalytics.trackAddToCart !== 'function') return;
      var source = this.getAttribute('data-track-source');
      if (!source) return;

      var track = window.SiteAnalytics.trackAddToCart.bind(window.SiteAnalytics);
      var oneTime = this.isOneTime();
      var bundle = this.shouldBundle();
      var bladesOnly = this.mainIsBlades && bundle;
      var pressed = this.querySelector('.freq-selector__opt[aria-pressed="true"]');
      var itemName = this.getAttribute('data-track-item-name') || '';
      var itemVariant = this.getAttribute('data-track-item-variant') || '';
      var planId = this.currentPlanId() || null;
      var cadence = oneTime ? null : (this.currentFrequencyCadence() || null);
      var cadenceExtra = cadence ? { frequency_cadence: cadence } : {};
      var bladesPriceCents = parseInt(this.getAttribute('data-blades-price'), 10) || this.bundlePrice || 0;
      var itemId;
      var priceCents;
      var sellingPlanId;

      if (bladesOnly) {
        itemId = this.bladesVariantId || this.variantId;
        itemName = this.getAttribute('data-track-blades-item-name') || itemName;
        itemVariant = this.getAttribute('data-track-blades-item-variant') || itemVariant;
        priceCents = bladesPriceCents;
        sellingPlanId = this.bladesSellingPlanId || null;
      } else if (oneTime) {
        itemId = (pressed && pressed.getAttribute('data-onetime-variant-id')) || this.variantId;
        priceCents = parseInt(pressed && pressed.getAttribute('data-onetime-price'), 10) || this.basePrice;
        sellingPlanId = null;
      } else {
        itemId = this.variantId;
        priceCents = bundle ? this.basePrice + bladesPriceCents : this.basePrice;
        sellingPlanId = bundle
          ? (this.bladesSellingPlanId || null)
          : (planId || null);
      }

      track(Object.assign({
        item_id: itemId,
        item_name: itemName,
        item_variant: itemVariant,
        source_component: source,
        price: priceCents / 100,
        selling_plan_id: sellingPlanId
      }, cadenceExtra));
    }

    addToCart() {
      // Belt-and-braces: the button is disabled / diverted to the waitlist, but never add an OOS variant.
      if (this.isOos) return Promise.reject(new Error('Variant is out of stock'));

      var oneTime = this.isOneTime();
      var pressed = this.querySelector('.freq-selector__opt[aria-pressed="true"]');

      // One-time: add the option's configured one-time product variant; otherwise the selected (subscription) variant.
      var razorVariantId;
      if (oneTime) {
        razorVariantId = parseInt(pressed && pressed.getAttribute('data-onetime-variant-id'), 10) || parseInt(this.variantId, 10);
      } else {
        razorVariantId = parseInt(this.variantId, 10);
      }
      if (!razorVariantId) return Promise.reject(new Error('Missing variant id'));

      var planId = parseInt(this.currentPlanId(), 10);
      var bundle = this.shouldBundle();
      // shouldBundle() is true for the add_blades / annual ("Occasionally") option. On the razor kit
      // that means "add the kit AND a 24-pack of blades" — two lines. On the blades quiz the product
      // already IS the blades, so there's no kit to add: the annual option should add ONLY the 24-pack.
      var bladesOnly = this.mainIsBlades && bundle;
      var token = String(Date.now()) + '-' + Math.random().toString(36).slice(2, 8);

      var razorItem = {
        id: razorVariantId,
        quantity: 1,
        properties: oneTime
          ? { '_razor_kit_onetime': 'true' }
          : (bundle
            ? { '_razor_kit_razor': token }
            : { '_razor_kit_frequency': true })
      };
      // One-time: never attach a selling plan (purchase at the regular one-time price).
      if (planId && !oneTime) razorItem.selling_plan = planId;
      // Blades quiz: the main line IS the blades product, so the cart drawer should treat it as a blades line.
      if (this.mainIsBlades && !bladesOnly) razorItem.properties['_blades'] = 'true';

      var items = [];
      if (!bladesOnly) items.push(razorItem);
      if (bundle) {
        var bladesItem = {
          id: parseInt(this.bladesVariantId, 10),
          quantity: 1,
          // Token-link to the kit when one is added; for a blades-only add just mark it as blades.
          properties: bladesOnly ? { '_blades': 'true' } : { '_razor_kit_blades': token, '_blades': 'true' }
        };
        var bladesSp = parseInt(this.bladesSellingPlanId, 10);
        if (bladesSp) bladesItem.selling_plan = bladesSp;
        items.push(bladesItem);
      }

      var routes = (window.themeVariables && window.themeVariables.routes) || {};
      var addUrl = routes.cartAddUrl ? routes.cartAddUrl + '.js' : '/cart/add.js';
      var getUrl = routes.cartUrl ? routes.cartUrl + '.js' : '/cart.js';
      var self = this;

      return fetch(addUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items: items, sections: ['mini-cart'] })
      })
        .then(function (response) {
          return response.json().then(function (json) { return { ok: response.ok, status: response.status, body: json }; });
        })
        .then(function (result) {
          if (!result.ok) {
            throw new Error((result.body && result.body.description) || 'Add to cart failed');
          }
          self.trackAddToCartAnalytics();
          var firstItem = (result.body.items && result.body.items[0]) || result.body;
          self.dispatchEvent(new CustomEvent('variant:added', {
            bubbles: true,
            detail: { variant: firstItem }
          }));
          return fetch(getUrl).then(function (r) { return r.json(); }).then(function (cart) {
            document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
              bubbles: true,
              detail: { cart: cart }
            }));
            cart.sections = result.body.sections;
            var openMiniCart = !!(window.themeVariables &&
              window.themeVariables.settings &&
              window.themeVariables.settings.cartType === 'drawer');
            document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
              bubbles: true,
              detail: { cart: cart, openMiniCart: openMiniCart }
            }));
            var miniCart = document.getElementById('mini-cart');
            if (miniCart && !miniCart.open) miniCart.open = true;
          });
        });
    }
  }

  customElements.define('frequency-selector', FrequencySelector);
})();
